import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileSpreadsheet } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
}

export default function TrialBalance() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/trial-balance`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = data.filter((item) =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalDebit = filtered.reduce((sum, a) => sum + (a.debit || 0), 0);
  const totalCredit = filtered.reduce((sum, a) => sum + (a.credit || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tb-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600" data-testid="tb-error">{error}</div>
    );
  }

  return (
    <div data-testid="trial-balance">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="tb-title">
          Trial Balance
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Showing {filtered.length} of {data.length} accounts
        </p>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-sm px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          data-testid="tb-search"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Debit</p>
            <p className="text-xl font-bold text-blue-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(totalDebit)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Credit</p>
            <p className="text-xl font-bold text-amber-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(totalCredit)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Account Balances
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[600px] overflow-auto">
            <Table data-testid="tb-table">
              <TableHeader>
                <TableRow className="bg-slate-50 sticky top-0">
                  <TableHead className="font-semibold">Account Name</TableHead>
                  <TableHead className="text-right font-semibold w-32">Opening</TableHead>
                  <TableHead className="text-right font-semibold w-32">Debit</TableHead>
                  <TableHead className="text-right font-semibold w-32">Credit</TableHead>
                  <TableHead className="text-right font-semibold w-32">Closing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((account) => (
                  <TableRow key={account.name} data-testid={`tb-row-${account.name}`}>
                    <TableCell className="font-medium text-sm">{account.name}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm">
                      {formatCurrency(account.opening)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm text-blue-600">
                      {formatCurrency(account.debit)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm text-amber-600">
                      {formatCurrency(account.credit)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm font-semibold">
                      {formatCurrency(account.closing)}
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      {data.length === 0 ? "No trial balance data available" : "No matching accounts"}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.length > 0 && (
                  <TableRow className="bg-slate-100 font-bold sticky bottom-0">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(filtered.reduce((s, a) => s + (a.opening || 0), 0))}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-blue-700">
                      {formatCurrency(totalDebit)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-amber-700">
                      {formatCurrency(totalCredit)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(filtered.reduce((s, a) => s + (a.closing || 0), 0))}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
